import { useMemo, useState } from "react";
import { Check, ChevronDown, Package, Search } from "lucide-react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const triggerCls =
  "flex min-h-10 w-full items-center justify-between gap-2 rounded-none border border-zinc-300 bg-white px-3 py-2 text-left text-sm focus:border-[#002FA7] focus:outline-none focus:ring-1 focus:ring-[#002FA7]";

const optionCls =
  "flex cursor-pointer items-start gap-2 rounded-none px-2 py-2 text-sm data-[selected=true]:bg-blue-50 data-[selected=true]:text-zinc-900";

function formatStock(material) {
  const stock = Number(material.current_stock || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 4,
  });
  return `stok: ${stock} ${material.unit || ""}`.trim();
}

/**
 * Searchable picker for the sales item row.
 * The value deliberately keeps the existing format: prod:<id> or mat:<id>.
 */
export default function ProductMaterialCombobox({
  products = [],
  materials = [],
  value = "",
  onChange,
  disabled = false,
  dataTestId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const options = useMemo(() => {
    const productOptions = products
      .filter((product) => product.active !== false)
      .map((product) => ({
        id: `prod:${product.id}`,
        kind: "product",
        name: product.name || "Tanpa nama",
        code: product.code || "",
        detail: product.pricing_mode === "per_area" ? "Harga / m²" : "Harga / pcs",
      }));

    const materialOptions = materials
      .filter((material) => material.active !== false)
      .map((material) => ({
        id: `mat:${material.id}`,
        kind: "material",
        name: material.name || "Tanpa nama",
        code: "",
        detail: formatStock(material),
      }));

    return { productOptions, materialOptions };
  }, [products, materials]);

  const normalizedQuery = query.trim().toLocaleLowerCase("id-ID");
  const matches = (option) =>
    !normalizedQuery ||
    `${option.name} ${option.code} ${option.detail}`
      .toLocaleLowerCase("id-ID")
      .includes(normalizedQuery);

  const filteredProducts = options.productOptions.filter(matches);
  const filteredMaterials = options.materialOptions.filter(matches);
  const selected = [...options.productOptions, ...options.materialOptions].find(
    (option) => option.id === value,
  );

  const choose = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Pilih produk atau bahan"
          data-testid={dataTestId}
          disabled={disabled}
          className={triggerCls}
        >
          <span className={selected ? "truncate text-zinc-900" : "truncate text-zinc-500"}>
            {selected ? (
              <>
                {selected.name}
                {selected.code ? ` [${selected.code}]` : ""}
              </>
            ) : (
              "— cari produk / bahan —"
            )}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
      >
        <Command shouldFilter={false} loop>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Ketik nama atau kode..."
          />
          <CommandList>
            <CommandEmpty>Tidak ada produk atau bahan yang cocok.</CommandEmpty>

            {filteredProducts.length > 0 && (
              <CommandGroup heading="Produk (Multi-Bahan / BOM)">
                {filteredProducts.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={option.id}
                    onSelect={() => choose(option.id)}
                    className={optionCls}
                  >
                    <Package className="mt-0.5 h-4 w-4 shrink-0 text-[#002FA7]" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">
                        {option.name}{option.code ? ` [${option.code}]` : ""}
                      </span>
                      <span className="block text-[11px] text-zinc-500">{option.detail}</span>
                    </span>
                    <Check
                      className={`${value === option.id ? "mt-0.5 h-4 w-4 shrink-0" : "hidden"}`}
                      aria-hidden="true"
                      />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {filteredMaterials.length > 0 && (
              <CommandGroup heading="Bahan Langsung">
                {filteredMaterials.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={option.id}
                    onSelect={() => choose(option.id)}
                    className={optionCls}
                  >
                    <Search className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{option.name}</span>
                      <span className="block text-[11px] text-zinc-500">{option.detail}</span>
                    </span>
                    <Check
                      className={`${value === option.id ? "mt-0.5 h-4 w-4 shrink-0" : "hidden"}`}
                      aria-hidden="true"
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
